import 'Components/vm-icon/vm-icon';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import Styles from './vm-video-isi.scss';

/**
 * Renders the Important Safety Information (ISI) block on the video detail page.
 *
 * Desktop is controlled by `displayIsiPreview`:
 * - `false` (default): inline card in the content column.
 * - `true` (forced floating, used for ALLOWED_ISI_CHANNELS): collapsible
 *   fixed-position bar pinned to the viewport bottom.
 *
 * On mobile the floating bar is always shown (regardless of `displayIsiPreview`).
 * When the `vm-video-info-bottom-sheet` opens it stacks above the floating bar
 * (the sheet's z-index is higher) and surfaces the ISI itself, so the floating bar
 * simply sits hidden behind it — no cross-component coordination required.
 */
@customElement('vm-video-isi')
export class VmVideoIsi extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'safety-information' }) safetyInformation = '';
    @property({ type: Boolean, attribute: 'display-isi-preview', reflect: true }) displayIsiPreview = false;

    @state() private _floatingExpanded = false;

    private toggleFloating = () => {
        this._floatingExpanded = !this._floatingExpanded;
    };

    private renderFloating() {
        return html`
            <div
                class=${classMap({
                    'vm-video-isi__floating': true,
                    'vm-video-isi__floating--expanded': this._floatingExpanded,
                })}
            >
                <button
                    type="button"
                    class="vm-video-isi__toggle"
                    aria-expanded=${Boolean(this._floatingExpanded)}
                    aria-label=${this._floatingExpanded ? 'Collapse safety information' : 'Expand safety information'}
                    @click=${this.toggleFloating}
                >
                    <vm-icon name="chevron-down" class="vm-video-isi__chevron-icon"></vm-icon>
                </button>
                <div class="vm-video-isi__floating-body">${unsafeHTML(this.safetyInformation)}</div>
            </div>
        `;
    }

    private renderInlineCard() {
        return html`<div class="vm-video-isi__card">${unsafeHTML(this.safetyInformation)}</div>`;
    }

    override render() {
        if (!this.safetyInformation) return nothing;
        // The inline card is desktop-only (default mode); the floating bar covers the
        // preview mode on desktop and every mode on mobile. CSS picks which is visible
        // per breakpoint, so both can render at once without overlapping.
        return html`${this.displayIsiPreview ? nothing : this.renderInlineCard()}${this.renderFloating()}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-isi': VmVideoIsi;
    }
}
